import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "checking", payment_status: null, amount: 0 });
  const attempts = useRef(0);
  const cancelRef = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    cancelRef.current = false;
    const poll = async () => {
      if (cancelRef.current) return;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState({ status: "success", payment_status: "paid", amount: (data.amount_total || 0) / 100 });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "failed", payment_status: data.payment_status });
          return;
        }
        attempts.current += 1;
        if (attempts.current >= 8) {
          setState({ status: "timeout", payment_status: data.payment_status });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ status: "error", payment_status: null });
      }
    };
    poll();
    return () => { cancelRef.current = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-8">
      <div className="max-w-md w-full bg-white border border-[#E2E8F0] rounded-md p-10 text-center">
        {state.status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-[#1F3A8A] animate-spin" />
            <h1 className="font-display text-2xl font-bold mb-2">Confirming payment…</h1>
            <p className="text-[#64748B] text-sm">Hang tight — this only takes a moment.</p>
          </>
        )}
        {state.status === "success" && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#14B8A6]/10 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-[#14B8A6]" strokeWidth={2.5} />
            </div>
            <h1 className="font-display text-3xl font-bold mb-2" data-testid="billing-success">Payment successful</h1>
            <p className="text-[#64748B] mb-6">${state.amount.toFixed(2)} processed. Tokens have been added to your wallet.</p>
            <div className="flex gap-2 justify-center">
              <Link to="/dashboard"><Button className="bg-[#1F3A8A] hover:bg-[#1E40AF] text-white">Go to dashboard</Button></Link>
              <Link to="/builder"><Button variant="outline" className="border-[#14B8A6] text-[#14B8A6]">Build a prompt</Button></Link>
            </div>
          </>
        )}
        {(state.status === "failed" || state.status === "error" || state.status === "timeout") && (
          <>
            <XCircle className="w-12 h-12 mx-auto mb-4 text-[#EF4444]" />
            <h1 className="font-display text-2xl font-bold mb-2">Something went wrong</h1>
            <p className="text-[#64748B] mb-6">{state.status === "timeout" ? "We couldn't confirm your payment in time." : "Your payment was not completed."}</p>
            <Link to="/plans"><Button className="bg-[#1F3A8A] hover:bg-[#1E40AF] text-white">Back to pricing</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
